/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Suspense } from 'react';
import {
    Outlet, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import { useExperiment, useRouteBoundary } from '@acp/feature/auth';
import {
    Flex, Link, Text,
} from '@chakra-ui/react';
import { FaIcon, ThemeTokens } from '@acp/core-ui';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { AppLoader } from '../components/app-loader';
import { UsernameLoginBanner } from '../components/username-login-banner';
import { AppLayout } from './app-layout';
import { NavigationProvider } from '../providers/navigation-provider';
import { useUserSettings } from '../features/settings';

/**
 * This component is used to wrap the routes that need to be protected by the authentication.
 */
export function RouteBoundary() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [isLoading, sessionExpired] = useRouteBoundary({
        agencyId: searchParams.get('__acp_agency_id'),
        onFailure: () => navigate(`/auth/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`, {
            replace: true,
        }),
    });

    const { isLoading: isUserSettingsLoading } = useUserSettings();
    const { isLoading: isExperimentsLoading } = useExperiment();

    if (isLoading || isUserSettingsLoading || isExperimentsLoading) {
        return <AppLoader />;
    }

    return (
        // This provider is used to keep track of the previous path
        <NavigationProvider>
            <Suspense fallback={<AppLoader />}>
                <AppLayout>
                    {sessionExpired ? (
                        <Flex
                            direction={['column', null, 'row']}
                            justifyContent="center"
                            alignItems="start"
                            px={4}
                            py={4}
                            bgColor="white"
                            borderBottomWidth={1}
                            borderBottomColor={ThemeTokens.Defaults.borderColor}
                            gap={[0, null, 1]}
                        >
                            <Text fontWeight="medium" color="danger.500">
                                <FaIcon icon={faTriangleExclamation} mr={1} color="danger.500" />
                                {' '}
                                We&apos;re sorry, your session has expired.
                            </Text>
                            {' '}
                            <Link
                                as="button"
                                onClick={() => navigate(`/auth/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`, {
                                    replace: true,
                                })}
                                textDecoration="none"
                            >
                                Click here to log back in.
                            </Link>
                        </Flex>
                    ) : (
                        <UsernameLoginBanner />
                    )}
                    <Outlet />
                </AppLayout>
            </Suspense>
        </NavigationProvider>
    );
}
